import { cn } from "@/lib/utils";
import { tagChipStyle, tagDotColor } from "@/lib/crm-fields/tag-colors";

interface TagChipProps {
  label: string;
  /** Stored hex; null/invalid falls back to a stable hue derived from `label`. */
  color: string | null | undefined;
  /** When provided, renders a × button that calls this on click. */
  onRemove?: () => void;
  /** aria-label for the remove button, e.g. `Remove VIP`. */
  removeLabel?: string;
  /** "sm" (text-xs, default) or "xs" (text-[10px]) for dense surfaces like kanban cards. */
  size?: "sm" | "xs";
  /** Leading solid-hue dot. Off by default — the tinted fill already conveys color. */
  showDot?: boolean;
  className?: string;
}

/**
 * Canonical tag chip: a tinted, hue-bordered pill that stays legible in both
 * themes (see lib/crm-fields/tag-colors). Used everywhere applied tags render
 * — the picker, the create dialogs, and kanban cards — so a single place owns
 * "what a tag looks like".
 */
export function TagChip({
  label,
  color,
  onRemove,
  removeLabel,
  size = "sm",
  showDot = false,
  className,
}: TagChipProps) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 font-medium",
        size === "xs" ? "text-[10px]" : "text-xs",
        className,
      )}
      style={tagChipStyle(color, label)}
    >
      {showDot && (
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ background: tagDotColor(color, label) }}
        />
      )}
      <span className="truncate" title={label}>
        {label}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          // Inherits the chip's hue-tinted text; dimmed so it reads as secondary.
          // Focus ring + brighten so keyboard users get a clear affordance.
          className="-mr-0.5 ml-0.5 shrink-0 rounded-full leading-none opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={removeLabel ?? `Remove ${label}`}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </span>
  );
}
